import { openModal } from '@kcd/app-interface/dist/web2app';
import { Button } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import type { OAISchema } from '~/types/openapi';
import { isMoreThan } from '~/utils/number';

import { useMembershipStatus } from '../../hooks/useMembershipStatus';
import { getBonusPackCouponAmountText } from '../../utils';

import {
  BonuspackCloverFill18x18Icon,
  MembershipStandard28x14Icon,
} from '~/assets/icons';

type Props = {
  isSomeChecked: boolean;
  discountedTotalAmount: OAISchema['MarketInShoppingCartPaymentResponse']['discountedTotalAmount'];
  membershipRegisteredExpectedAmount?: number;
};

export function CartOrderMembershipNudge({
  isSomeChecked,
  discountedTotalAmount,
  membershipRegisteredExpectedAmount = 0,
}: Props) {
  const {
    isTrialAvailable,
    isAvailableToSubscribe,
    isAvailableToSubscribeBonusPack,
    getRegisterMembershipUrl,
  } = useMembershipStatus();

  const bonusPackCouponAmountText = getBonusPackCouponAmountText();

  const nudgeType = (() => {
    if (isTrialAvailable) {
      return 'plus_membership_free';
    }

    if (isAvailableToSubscribe) {
      return 'plus_membership';
    }

    if (isAvailableToSubscribeBonusPack) {
      return 'lucky_bonus_pack';
    }

    return '';
  })();

  const onClickRegisterMembership = () => {
    const registerUrl = getRegisterMembershipUrl();

    trackEvent('clicked button in 마켓/장바구니/멤버십넛지', {
      clicked_area: 'cta_button_nudge',
      membership_nudge_type: nudgeType,
    });

    if (registerUrl) {
      openModal({
        url: registerUrl,
      });
    }
  };

  const NudgeContent = (() => {
    const isMoreThanDiscountedTotalAmount = isMoreThan(
      discountedTotalAmount,
      membershipRegisteredExpectedAmount,
    );

    if (isSomeChecked) {
      // 플멤 쿠폰 할인가가 일반 쿠폰 할인가보다 더 높다면 또는 둘의 가격이 같다면 보여주지 않는다
      if (
        !isMoreThanDiscountedTotalAmount ||
        discountedTotalAmount === membershipRegisteredExpectedAmount
      )
        return null;

      if (isTrialAvailable) {
        return (
          <p className="flex items-center gap-4 text-accent typo-title100-semibold">
            <MembershipStandard28x14Icon />
            구독하고 {membershipRegisteredExpectedAmount?.toLocaleString()}원
            주문하기
          </p>
        );
      }

      if (isAvailableToSubscribe) {
        return (
          <p className="flex items-center gap-4 text-accent typo-title100-semibold">
            <MembershipStandard28x14Icon />
            구독하고 {membershipRegisteredExpectedAmount?.toLocaleString()}원
            주문하기
          </p>
        );
      }

      if (isAvailableToSubscribeBonusPack) {
        return (
          <p className="flex items-center gap-4 text-accent typo-title100-semibold">
            <BonuspackCloverFill18x18Icon />
            혜택 받고 {membershipRegisteredExpectedAmount?.toLocaleString()}원
            결제하기
          </p>
        );
      }
    } else {
      if (isTrialAvailable) {
        return (
          <p className="text-accent typo-title100-semibold">
            총 6만원 쿠폰팩 첫 4주 무료체험
          </p>
        );
      }

      if (isAvailableToSubscribe) {
        return (
          <p className="text-accent typo-title100-semibold">
            멤버십 전용 총 6만원 쿠폰팩 보러가기
          </p>
        );
      }

      if (isAvailableToSubscribeBonusPack) {
        return (
          <p className="text-accent typo-title100-semibold">
            총 {bonusPackCouponAmountText} 쿠폰 행운 보너스팩 보러가기
          </p>
        );
      }
    }

    return null;
  })();

  if (!NudgeContent) return null;

  return (
    <Button
      className="!mb-8 flex w-full items-center justify-center"
      variant="outlineBlue"
      size="lg"
      onClick={onClickRegisterMembership}
      type="button"
    >
      {NudgeContent}
    </Button>
  );
}
